import { defineStore } from 'pinia';
import { useOrganizationStore } from './organizationStore';
import { apiRequestHandler } from '@/utils/api';
import {
  Cadence,
  CreateCadence,
  CreateTimeBox,
  TimeBox,
  UpdateCadence,
  UpdateTimeBox,
  BoardService,
} from '@/api';
import { orderBy } from 'lodash-es';
import { timeout } from '@/utils/time';
import { getCadenceProvider } from '@/utils/boards';

export interface CadenceState {
  cadences: Cadence[];
  timeboxes: TimeBox[];
}

export const useCadenceStore = defineStore('cadence', {
  /****
   STATE
   ****/
  state: (): CadenceState => ({
    cadences: [],
    timeboxes: [],
  }),

  /****
   GETTERS
   ****/
  getters: {
    categorizedCadences(): {
      /** all cadences, including archived, excluding provider cadences */
      all: Cadence[];
      active: Cadence[];
      archived: Cadence[];
      topoSortedAll: (Cadence & { level: number })[];
      topoSortedActive: (Cadence & { level: number })[];
    } {
      const all: Cadence[] = [
        ...this.cadences.filter((c) => !getCadenceProvider(c)),
      ];
      const active = all.filter((c) => !c.archived);
      const archived = all.filter((c) => c.archived);

      const topoSort = (cadences: Cadence[]) => {
        const visited = new Set<Cadence['id']>();
        const dfs = (cadence: Cadence, level: number) => {
          if (visited.has(cadence.id)) return;
          visited.add(cadence.id);

          topoSorted.push({ ...cadence, level });

          const children = orderBy(
            cadences.filter((c) => c.parentId === cadence.id),
            (c) => c.label,
            'asc'
          );
          for (const child of children) dfs(child, level + 1);
        };
        const topoSorted: (Cadence & { level: number })[] = [];

        for (const cadence of orderBy(cadences, (c) => c.label, 'asc')) {
          if (cadence.parentId) continue;
          dfs(cadence, 0);
        }
        return topoSorted;
      };

      const topoSortedAll: (Cadence & { level: number })[] = topoSort(all);
      const topoSortedActive: (Cadence & { level: number })[] =
        topoSort(active);

      return {
        /** all cadences, including archived, excluding provider cadences */
        all,
        /** active cadences, excluding archived */
        active,
        /** archived cadences only */
        archived,
        /** all cadences, including archived, topologically sorted */
        topoSortedAll,
        /** active cadences, excluding archived, topologically sorted */
        topoSortedActive,
      };
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    storeCadences(cadences: Cadence[], { overwrite = false } = {}) {
      if (overwrite) {
        this.cadences = cadences;
        return;
      }
      // add/update cadences
      cadences.forEach((cadence) => {
        const index = this.cadences.findIndex((c) => c.id === cadence.id);
        if (index >= 0) this.cadences[index] = cadence;
        else this.cadences.push(cadence);
      });
    },
    storeTimeboxes(timeboxes: TimeBox[]) {
      // add/update timeboxes
      timeboxes.forEach((timebox) => {
        const index = this.timeboxes.findIndex((t) => t.id === timebox.id);
        if (index >= 0) this.timeboxes[index] = timebox;
        else this.timeboxes.push(timebox);
      });
    },

    async createCadence(data: CreateCadence) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.createCadence<true>({
          path: { organizationId },
          body: data,
        });
        const cadence = response.data.data;
        this.storeCadences([cadence]);

        return cadence;
      });
    },

    async updateCadence(cadenceId: Cadence['id'], data: UpdateCadence) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(
        async () => {
          const response = await BoardService.updateCadence<true>({
            path: { organizationId, cadenceId },
            body: data,
          });
          const cadence = response.data.data;
          this.storeCadences([cadence]);
          return cadence;
        },
        {
          errorCallback: (err) => {
            throw err;
          },
        }
      );
    },

    async findCadences({ withTimeboxes = false } = {}) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const promises: [
          ReturnType<typeof BoardService.findCadences>,
          ReturnType<typeof this.findTimeBoxes> | undefined
        ] = [
          BoardService.findCadences<true>({
            path: { organizationId },
            query: { includeArchived: true },
          }),
          undefined,
        ];
        if (withTimeboxes) {
          promises[1] = this.findTimeBoxes();
        }
        const responses = await Promise.all(promises);
        const cadences = responses[0].data?.data || [];
        this.storeCadences(cadences, { overwrite: true });
        return cadences;
      });
    },

    async archiveCadence(cadenceId: Cadence['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.archiveCadence<true>({
          path: { organizationId, cadenceId },
        });

        await timeout(3000); // backend does not update immediately
        await this.findCadences();

        return response.data;
      });
    },

    async unarchiveCadence(cadenceId: Cadence['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.unarchiveCadence<true>({
          path: { organizationId, cadenceId },
        });
        await timeout(3000); // backend does not update immediately
        await this.findCadences();

        return response.data;
      });
    },

    async createTimeBox(data: CreateTimeBox) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.createTimeBox<true>({
          path: { organizationId },
          body: data,
        });

        const recursiveTimeboxes: TimeBox[] = [];
        const flatten = (timebox: TimeBox) => {
          recursiveTimeboxes.push(timebox);
          timebox.children.forEach((child) => flatten(child));
        };
        flatten(response.data.data);

        this.storeTimeboxes(recursiveTimeboxes);
        return recursiveTimeboxes;
      });
    },

    async updateTimeBox(timeBoxId: TimeBox['id'], data: UpdateTimeBox) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.updateTimeBox<true>({
          path: { organizationId, timeBoxId },
          body: data,
        });
        const recursiveTimeboxes: TimeBox[] = [];
        const flatten = (timebox: TimeBox) => {
          recursiveTimeboxes.push(timebox);
          timebox.children.forEach((child) => flatten(child));
        };
        flatten(response.data.data);
        this.storeTimeboxes(recursiveTimeboxes);

        return recursiveTimeboxes;
      });
    },

    async findTimeBoxes() {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.findTimeBoxes<true>({
          path: { organizationId },
        });
        const timeboxes = response.data;
        const recursiveTimeboxes: TimeBox[] = [];
        const flatten = (timebox: TimeBox) => {
          recursiveTimeboxes.push(timebox);
          timebox.children.forEach((child) => flatten(child));
        };
        timeboxes.data.forEach((timebox: TimeBox) => flatten(timebox));
        this.storeTimeboxes(recursiveTimeboxes);
        return timeboxes;
      });
    },

    async deleteTimeBox(
      cadenceId: string,
      timeBoxId: TimeBox['id'],
      moveToTimeBoxId: TimeBox['id'] | null
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.deleteTimeBox<true>({
          path: { organizationId, cadenceId, timeBoxId },
          body: { moveToTimeBoxId },
        });

        const index = this.timeboxes.findIndex((t) => t.id === timeBoxId);
        if (index >= 0) this.timeboxes.splice(index, 1);

        return response.data;
      });
    },
  },
});
